/* eslint-disable no-nested-ternary */
/* eslint-disable prefer-destructuring */
import classes from "./SectionFeaturedDesktop.module.scss";
import { FC, useRef, useState } from "react";
import BreadCrumbs from "@Components/BreadCrumbs";
import { getRegionBreadcrumb } from "@Pages/Search/components/SearchHeadingContent/constant";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import PropertyPageDispatcher from "@Pages/PropertyPage/data/actions";
import { AppState } from "@Store/index";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { PAGE, CANNIBALIZATION_ROLLOUT } from "constants/configConstants";
import Image from "@Components/Image";
import getFeaturedImage from "@Utils/getFeaturedImage";
import getImagePath from "@Utils/getImagePath";
import {
  getItemsByCategories,
  getGalleryTabFor3DViews,
  getVideoSrc,
  getVideoThumb,
  videoElementWithoutYoutube,
} from "../SectionFeatured.tsx/utils";
import Carousel from "@Components/Carousel";
import CarouselIndicator from "@Components/Carousel/components/CarouselIndicator";
// import TrustPilotBadge from "@Components/TrustPilotBadge";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import VideoPlayer from "@Components/VideoPlayer";
import ThreeDIcon from "@Icons/ThreeDIcon";
import VideoPlayIcon from "@Icons/VideoPlayIcon";
import CarouselChevronIcon from "@Icons/CarouselChevronIcon";
import wrapperHOC from "@Utils/wrapperHOC";
import PropertyTags from "@Pages/Search/components/SearchListItem/components/PropertyTags";
import { transformVideoPath } from "../ReviewsSection/data/helper";
import useWhitelabel from "hooks/useWhitelabel";
import { convertToWhitelabelURL } from "@Pages/WhitelabelHome/Desktop/helpers/WhiteLabelRedirectionHelper";
import config from "@Config/index";
import useTranslationHook from "hooks/useTranslationHook";
import WhiteLabelBreadCrumbs from "@Pages/WhitelabelHome/components/WhiteLabelBreadCrumbs/WhiteLabelBreadCrumbs";

const SectionFeaturedDesktop: FC<{
  isLoading: boolean;
  isBookFormEnabledForInactiveProperty: boolean;
}> = ({ isLoading, isBookFormEnabledForInactiveProperty = false }) => {
  const playerRef: any = useRef(null);
  const dispatch = useDispatch();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);
  const { data, reviewData, roomTypesData } = useSelector(
    ({ propertyPageData }: AppState) => ({
      data: propertyPageData.data,
      reviewData: propertyPageData.reviewData?.result,
      roomTypesData: propertyPageData?.roomTypes?.data || [],
    }),
    shallowEqual,
  );
  const { isWhitelabel, isDemoSubdomain, partner_uuid, isUniversityPartner, whitelabelConfig } =
    useWhitelabel();
  const { t } = useTranslationHook();
  const { userLanguage } = useSelector(
    ({ appData }: AppState) => ({
      userLanguage: appData.userLanguage,
    }),
    shallowEqual,
  );

  // Show breadcrumbs for country/state pages
  const isCountryOrStatePage = ["country", "state"].includes(data.region?.region_type);
  const shouldHideBreadcrumbsForCountryState =
    isCountryOrStatePage &&
    !CANNIBALIZATION_ROLLOUT.includes(data.region?.location?.country?.short_name || "");

  const breadcrumbs = shouldHideBreadcrumbsForCountryState
    ? []
    : getRegionBreadcrumb(
        data.region,
        { parent: true },
        data.region?.location?.country?.short_name,
      );

  const getSearchQueryParams = () => {
    if (typeof window === "undefined") return "";
    const urlParams = new URLSearchParams(window.location.search);
    const searchParams = new URLSearchParams();

    const filterParams = [
      "range_available_price",
      "meta_types",
      "meta_min_lease_duration",
      "move_in_month",
      "move_in_date",
      "move_out_date",
      "price_min",
      "price_max",
      "room_type",
      "amenities",
      "region_radius",
      "partner_id",
    ];
    filterParams.forEach((param) => {
      const value = urlParams.get(param);
      if (value) searchParams.set(param, value);
    });

    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : "";
  };

  const shouldHideBreadcrumbs =
    isWhitelabel &&
    isUniversityPartner &&
    whitelabelConfig?.is_university_properties_section_enabled;
  const rentalsText = t("propertypage.sectionfeatured.rentals");
  const featuredImage = data?.images?.length ? getFeaturedImage(data?.images) : {};
  const images = [...data.images];
  const imgAltPrefix = `${data?.name} - ${data?.region?.combined_name}`;

  data.children_short?.forEach((child: any) => {
    if (child.images?.length > 0) {
      images.push(...child.images);
    }
  });
  let imagesWithoutFeaturedImage = [];
  if (featuredImage) {
    imagesWithoutFeaturedImage = images.filter(
      (image) => image.base_path != featuredImage.base_path,
    );
  }
  const showVideoTour: any = videoElementWithoutYoutube(data);
  const filteredVideos = data?.videos?.filter((video: any) => video.type === "vo_tour");
  const sortedVideos = filteredVideos?.sort(
    (v1: any, v2: any) => parseFloat(v2?.duration) - parseFloat(v1?.duration),
  );
  const videoItem = sortedVideos?.length
    ? sortedVideos[0]
    : showVideoTour.length
    ? showVideoTour[0]
    : null;
  const show3DTour =
    data.virtual_views?.length > 0
      ? true
      : data.children_short?.some((child: any) => child.virtual_views?.length > 0);
  const allImages = imagesWithoutFeaturedImage.map((image) => image);
  allImages.unshift(featuredImage);

  let extraImages = 3;
  if (showVideoTour.length) extraImages -= 1;
  if (show3DTour) extraImages -= 1;

  const onImageClick = (tab = "") => {
    const galleryTab = tab === "3D" ? getGalleryTabFor3DViews(data, roomTypesData) : tab;
    const filteredReview =
      reviewData?.flatMap(({ media, rating, user_details }: any) =>
        (media || []).map(({ path, thumbnail_url }: any) => ({
          path: transformVideoPath(path),
          base_path: transformVideoPath(path),
          category: thumbnail_url ? "videos" : "images",
          platform: "gumlet",
          thumbnail_url: thumbnail_url ? transformVideoPath(thumbnail_url) : null,
          userName: user_details?.name,
          rating,
          showCaption: true,
        })),
      ) || [];
    const filteredSitePlan =
      data?.site_plan?.map(
        ({ building, number_of_floors, floor_details: [...floor_details_temp] }: any) => ({
          building,
          number_of_floors,
          floor_details: floor_details_temp?.sort(
            (a: any, b: any) => parseFloat(a?.floor) - parseFloat(b?.floor),
          ),
        }),
      ) || [];

    const additionData: any = {};
    if (data?.site_plan?.length > 0) {
      additionData["site_plan"] = filteredSitePlan;
    }
    if (filteredReview.length > 0) {
      additionData.shot_by_residents = filteredReview;
    }
    const _galleryItems = { ...getItemsByCategories(data), ...(additionData || {}) };
    const { floor_plan, site_plan, ...galleryItems } = _galleryItems;
    if (floor_plan) galleryItems["floor_plan"] = floor_plan;
    if (site_plan) galleryItems["site_plan"] = site_plan;
    propertyPageDispatcher.showGalleryModal({
      show: true,
      selectedTab: galleryTab || "all",
      data: galleryItems,
    });
    if (tab) {
      gtagEventEmitter({
        action: "clicked_object",
        category: PAGE.INVENTORY,
        label: tab,
      });
    } else {
      gtagEventEmitter({
        action: "clicked_photos",
        category: PAGE.INVENTORY,
        label: "photo_section",
      });
    }
  };

  const handleOnVideoHover = () => {
    const videoElement = playerRef?.current;

    if (!videoElement) {
      console.log("playerRef is not initialized");
      return;
    }

    try {
      // Safari-specific: Ensure muted and playsinline
      videoElement.muted = true;
      videoElement?.play().catch((error: any) => {
        console.log("Autoplay was prevented:", error);
      });
    } catch (e) {
      console.log("Error while trying to play the video:", e);
    }
    setIsVideoPlaying(true);
  };

  const handleOnVideoHoverLeave = () => {
    playerRef?.current?.pause();
    setIsVideoPlaying(false);
  };

  const [activePos, setActivePos] = useState(1);
  const handleChangeSlide = (pos: number) => setActivePos(pos);

  const extraImagePath = getImagePath(
    imagesWithoutFeaturedImage[imagesWithoutFeaturedImage?.length - 1]?.path,
    "medium",
  );

  const extraImageCaption = getImagePath(
    imagesWithoutFeaturedImage[extraImages - 1]?.caption,
    "medium",
  );

  return (
    <div className={classes.container}>
      <div className={formatClassNames(classes.topContainer, isLoading && classes.fallbackHeight)}>
        {isWhitelabel ? (
          <WhiteLabelBreadCrumbs
            pageType="property"
            regionName={data?.region?.name}
            regionSlug={data?.region?.canonical_name}
            propertyName={data?.name}
            className={classes.breadCrumb}
            searchQueryParams={getSearchQueryParams()}
          />
        ) : (
          !shouldHideBreadcrumbs &&
          breadcrumbs.length > 0 && (
            <BreadCrumbs
              data={
                CANNIBALIZATION_ROLLOUT.includes(data.region?.location?.country?.short_name || "")
                  ? [
                      ...breadcrumbs.map(({ link, name }: any) => {
                        let breadcrumbLink = link?.toString();
                        if (isWhitelabel) {
                          breadcrumbLink = convertToWhitelabelURL(
                            breadcrumbLink,
                            isDemoSubdomain,
                            partner_uuid,
                          );
                        }
                        return {
                          link: breadcrumbLink,
                          name: `${name}${
                            config.IS_CHINA_APP
                              ? userLanguage === "zh"
                                ? rentalsText
                                : ` ${rentalsText}`
                              : ""
                          }`,
                        };
                      }),
                      {
                        link: `/places/${data?.canonical_name}`,
                        name: data?.name || "",
                      },
                    ]
                  : breadcrumbs.map(({ link, name }: any) => {
                      let breadcrumbLink = link?.toString();
                      if (isWhitelabel) {
                        breadcrumbLink = convertToWhitelabelURL(
                          breadcrumbLink,
                          isDemoSubdomain,
                          partner_uuid,
                        );
                      }
                      return {
                        link: breadcrumbLink,
                        name: `${name}${
                          config.IS_CHINA_APP
                            ? userLanguage === "zh"
                              ? rentalsText
                              : ` ${rentalsText}`
                            : ""
                        }`,
                      };
                    })
              }
              dataTestId="searchpage-breadcrumb"
              isPropertyPage
              hideLastItem={CANNIBALIZATION_ROLLOUT.includes(
                data.region?.location?.country?.short_name || "",
              )}
            />
          )
        )}
      </div>
      <div className={classes.gridContainer}>
        <div className={classes.heroImageColumn} onClick={() => onImageClick("")} role="none">
          <PropertyTags
            inventory={data}
            dataTestId="propertyPage-property-soldOut-tag"
            isBookFormEnabledForInactiveProperty={isBookFormEnabledForInactiveProperty}
          />
          <Carousel
            showNavSlider
            className={classes.carousel}
            leftNavSliderClassname={classes.leftNavArrow}
            rightNavSliderClassname={classes.rightNavArrow}
            arrowColor="#111928"
            customArrowIcon={<CarouselChevronIcon width="16" height="16" fill="#111928" />}
            slideList={allImages.map((img, slideIndex) => (
              <div className={classes.imgWrap} key={`${imgAltPrefix} - ${img?.caption}` || ""}>
                <Image
                  isEagerLoad={slideIndex === 0}
                  alt={`${imgAltPrefix} - ${img?.caption}` || ""}
                  src={getImagePath(img?.path, "gallery-image-medium", { q: 70 })}
                  width="100%"
                  height="100%"
                  className={classes.heroImage}
                  dataTestId="propertypage-gallery-thumbnail-image"
                />
              </div>
            ))}
            activePos={activePos}
            handleChangeSlide={handleChangeSlide}
          />
          <CarouselIndicator
            activeDot={activePos}
            totalSlider={allImages.length}
            handleClickIndicator={handleChangeSlide}
            indicatorWrapperClassName={classes.indicator}
            type="line"
            dotColor="white"
            addInfinite
          />
          {/* <div className={classes.carouselIconsContainer}>{!isLoading && <TrustPilotBadge />}</div> */}
        </div>
        <div className={classes.featuredImagesColumn}>
          {imagesWithoutFeaturedImage.slice(1, extraImages).map((image) => {
            const path = getImagePath(image?.path, "medium");
            return (
              <div
                key={`${imgAltPrefix} - ${image?.caption}` || ""}
                className={classes.imageColumn}
                onClick={() => onImageClick("")}
                role="none"
              >
                <Image
                  className={classes.otherImages}
                  loading="eager"
                  src={path}
                  alt={`${imgAltPrefix} - ${image?.caption}` || ""}
                  height="100%"
                  width="100%"
                  isNotLazy
                />
              </div>
            );
          })}
          {showVideoTour.length > 0 && (
            <>
              {videoItem ? (
                <div
                  className={classes.imageColumn}
                  onMouseEnter={handleOnVideoHover}
                  onMouseLeave={handleOnVideoHoverLeave}
                  onClick={() => onImageClick("videos")}
                  role="none"
                >
                  <VideoPlayer
                    name="virtual_video"
                    videoUrl={getVideoSrc(videoItem)}
                    posterUrl={getVideoThumb(videoItem)}
                    noControls
                    containerClassName={classes.imageColumn}
                    customRef={playerRef}
                    muted
                    playsInline
                    inLoop
                    isLazyLoaded
                  />
                  {!isVideoPlaying && (
                    <div className={classes.overlay}>
                      <VideoPlayIcon size="clamp(20px, 2.8vw, 40px)" fill="white" />
                    </div>
                  )}
                </div>
              ) : (
                // extraImagePath if videos dont exist.
                <div className={classes.imageColumn} onClick={() => onImageClick("")} role="none">
                  <Image
                    className={classes.otherImages}
                    loading="eager"
                    src={extraImagePath}
                    alt={extraImageCaption}
                    height="100%"
                    width="100%"
                    isNotLazy
                  />
                </div>
              )}
            </>
          )}
          {show3DTour && (
            <div className={classes.imageColumn} onClick={() => onImageClick("3D")} role="none">
              <Image
                className={classes.otherImages}
                loading="eager"
                src={getImagePath(imagesWithoutFeaturedImage[0]?.path, "medium")}
                alt={`${imgAltPrefix} - ${imagesWithoutFeaturedImage[0]?.caption}` || ""}
                height="100%"
                width="100%"
                isNotLazy
              />
              <div className={classes.overlay}>
                <ThreeDIcon />
              </div>
            </div>
          )}
          <div className={classes.imageColumn} onClick={() => onImageClick("")} role="none">
            <Image
              className={classes.otherImages}
              loading="eager"
              src={extraImagePath}
              alt={extraImageCaption || imgAltPrefix || ""}
              height="100%"
              width="100%"
              isNotLazy
            />
            <div className={classes.overlay}>+{images.length}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default wrapperHOC(SectionFeaturedDesktop, {
  componentName: "SectionFeaturedDesktop-PropertyPage",
  showForChina: true,
});
